import React, { useEffect, useState, memo } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { FaTasks } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

const TASK_TYPES = [
  { key: "HOMEWORK",          label: "Homework" },
  { key: "QUIZ",              label: "Quiz" },
  { key: "WRITING_TASK",      label: "Writing Task" },
  { key: "CLASS_TEST",        label: "Class Test" },
  { key: "SPEAKING_PRACTICE", label: "Speaking" },
] as const;

const WEEKS = Array.from({ length: 16 }, (_, i) => i + 1);
const MOBILE_PER_PAGE = 4;
const TOTAL_PAGES = Math.ceil(16 / MOBILE_PER_PAGE); // 4

const weekLabel = (startingDate: string | null, weekNum: number): string => {
  if (!startingDate) return `W${weekNum}`;
  const d = new Date(startingDate);
  d.setDate(d.getDate() + (weekNum - 1) * 7);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const currentWeekNum = (startingDate: string | null): number => {
  if (!startingDate) return 1;
  const diff = Date.now() - new Date(startingDate).getTime();
  if (diff < 0) return 1;
  return Math.min(Math.floor(diff / (7 * 24 * 60 * 60 * 1000)) + 1, 16);
};

const defaultPage = (startingDate: string | null): number =>
  Math.min(Math.floor((currentWeekNum(startingDate) - 1) / MOBILE_PER_PAGE), TOTAL_PAGES - 1);

interface CheckinDto {
  weekNumber: number;
  taskType: string;
  checked: boolean;
}

interface BatchTrackerDto {
  batchId: number;
  batchName: string;
  startingDate: string | null;
  checkins: CheckinDto[];
}

type CheckinMap = Map<string, boolean>;

const makeKey = (week: number, task: string) => `${week}_${task}`;

const CheckboxCell = memo(({
  batchId, w, task, map, saving, onToggle,
}: {
  batchId: number; w: number; task: typeof TASK_TYPES[number];
  map: CheckinMap; saving: string | null;
  onToggle: (batchId: number, w: number, taskKey: string) => void;
}) => {
  const cellKey = makeKey(w, task.key);
  const ticked = map.get(cellKey) ?? false;
  const isSaving = saving === `${batchId}_${cellKey}`;
  return (
    <button
      onClick={() => onToggle(batchId, w, task.key)}
      disabled={isSaving}
      title={`Week ${w} – ${task.label}`}
      className={`w-7 h-7 rounded-full border transition-all flex items-center justify-center mx-auto ${
        isSaving
          ? "opacity-50 cursor-wait"
          : ticked
          ? "bg-green-500 border-green-500 text-white hover:bg-green-600"
          : "bg-white border-gray-300 hover:border-[#009fe3] hover:bg-blue-50"
      }`}
    >
      {ticked && (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      )}
    </button>
  );
});

const buildCheckinMap = (checkins: CheckinDto[]): CheckinMap => {
  const map = new Map<string, boolean>();
  checkins.forEach((c) => map.set(makeKey(c.weekNumber, c.taskType), c.checked));
  return map;
};

const API = process.env.REACT_APP_API_BASE_URL;

const MentorTaskCheckinPage: React.FC = () => {
  const [batches, setBatches] = useState<BatchTrackerDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localMaps, setLocalMaps] = useState<Record<number, CheckinMap>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [mobilePages, setMobilePages] = useState<Record<number, number>>({});

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/mentor/task-checkins`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load checklist");
      const json: BatchTrackerDto[] = await res.json();
      setBatches(json);
      const maps: Record<number, CheckinMap> = {};
      const pages: Record<number, number> = {};
      json.forEach((b) => {
        maps[b.batchId] = buildCheckinMap(b.checkins);
        pages[b.batchId] = defaultPage(b.startingDate);
      });
      setLocalMaps(maps);
      setMobilePages(pages);
    } catch (e: any) {
      setError(e.message ?? "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleToggle = async (batchId: number, weekNumber: number, taskType: string) => {
    const key = makeKey(weekNumber, taskType);
    const current = localMaps[batchId]?.get(key) ?? false;
    const next = !current;

    setLocalMaps((prev) => {
      const map = new Map(prev[batchId] ?? new Map());
      map.set(key, next);
      return { ...prev, [batchId]: map };
    });

    setSaving(`${batchId}_${key}`);
    try {
      const res = await fetch(`${API}/api/mentor/task-checkins`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ batchId, weekNumber, taskType, checked: next }),
      });
      if (!res.ok) throw new Error("Save failed");
    } catch {
      setLocalMaps((prev) => {
        const map = new Map(prev[batchId] ?? new Map());
        map.set(key, current);
        return { ...prev, [batchId]: map };
      });
    } finally {
      setSaving(null);
    }
  };

  const setPage = (batchId: number, page: number) =>
    setMobilePages((prev) => ({ ...prev, [batchId]: page }));

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        icon={<FaTasks />}
        title="Weekly Task Checklist"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/mentor/dashboard" },
          { label: "Task Checklist" },
        ]}
      />

      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {loading && <div className="text-center py-16 text-gray-500">Loading your checklist…</div>}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
        )}
        {!loading && !error && batches.length === 0 && (
          <div className="text-center py-16 text-gray-400">No batches assigned to you.</div>
        )}

        {!loading && batches.map((batch) => {
          const map = localMaps[batch.batchId] ?? new Map();
          const curWeek = currentWeekNum(batch.startingDate);
          const completedWeeks = Math.max(curWeek - 1, 0);
          const checked = Array.from(map.values()).filter(Boolean).length;
          const total = TASK_TYPES.length * completedWeeks;
          const pct = total === 0 ? 0 : Math.min(Math.round((checked / total) * 100), 100);
          const pctColor = pct >= 80 ? "text-green-600" : pct >= 40 ? "text-yellow-600" : "text-red-500";
          const barColor = pct >= 80 ? "bg-green-500" : pct >= 40 ? "bg-yellow-400" : "bg-red-400";
          const page = mobilePages[batch.batchId] ?? 0;
          const mobileWeeks = WEEKS.slice(page * MOBILE_PER_PAGE, page * MOBILE_PER_PAGE + MOBILE_PER_PAGE);

          return (
            <div key={batch.batchId} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Batch header */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-sm sm:text-base font-bold text-gray-800 truncate">{batch.batchName}</h2>
                  {batch.startingDate && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Started {new Date(batch.startingDate).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <div className={`text-xs sm:text-sm font-bold ${pctColor}`}>{checked}/{total} ({pct}%)</div>
                  <div className="w-20 sm:w-28 h-1.5 bg-gray-200 rounded-full mt-1">
                    <div className={`h-1.5 rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </div>

              {/* ── MOBILE TABLE (paginated, 4 weeks at a time) ── */}
              <div className="sm:hidden px-3 py-3">
                {/* Page nav */}
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={() => setPage(batch.batchId, page - 1)}
                    disabled={page === 0}
                    className="flex items-center gap-1 text-xs font-medium text-[#009fe3] disabled:text-gray-300 disabled:cursor-not-allowed"
                  >
                    <FiChevronLeft size={16} /> Prev
                  </button>
                  <span className="text-xs text-gray-500 font-medium">
                    Weeks {page * MOBILE_PER_PAGE + 1}–{Math.min(page * MOBILE_PER_PAGE + MOBILE_PER_PAGE, 16)}
                    <span className="text-gray-400"> of 16</span>
                  </span>
                  <button
                    onClick={() => setPage(batch.batchId, page + 1)}
                    disabled={page === TOTAL_PAGES - 1}
                    className="flex items-center gap-1 text-xs font-medium text-[#009fe3] disabled:text-gray-300 disabled:cursor-not-allowed"
                  >
                    Next <FiChevronRight size={16} />
                  </button>
                </div>

                {/* Mobile table */}
                <table className="text-xs border-collapse w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-2 py-2 text-left font-semibold text-gray-600 border border-gray-200 w-[100px]">
                        Task
                      </th>
                      {mobileWeeks.map((w) => (
                        <th
                          key={w}
                          className={`px-1 py-2 text-center font-medium border border-gray-200 ${
                            w === curWeek ? "bg-blue-50 text-[#009fe3]" : "text-gray-500"
                          }`}
                        >
                          <div>{weekLabel(batch.startingDate, w)}</div>
                          {w === curWeek && <div className="text-[9px] text-[#009fe3] font-bold">now</div>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {TASK_TYPES.map((task, idx) => (
                      <tr key={task.key} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50/40"}>
                        <td className="px-2 py-2 font-medium text-gray-700 border border-gray-200 text-[11px] leading-tight">
                          {task.label}
                        </td>
                        {mobileWeeks.map((w) => (
                          <td key={w} className={`py-1.5 text-center border border-gray-100 ${w === curWeek ? "bg-blue-50/30" : ""}`}>
                            <CheckboxCell batchId={batch.batchId} w={w} task={task} map={map} saving={saving} onToggle={handleToggle} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Dot pagination */}
                <div className="flex justify-center gap-1.5 mt-3">
                  {Array.from({ length: TOTAL_PAGES }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(batch.batchId, i)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        i === page ? "bg-[#009fe3] w-4" : "bg-gray-300"
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* ── DESKTOP TABLE (all 16 weeks) ── */}
              <div className="hidden sm:block overflow-x-auto px-4 py-4">
                <table className="text-xs border-collapse w-full" style={{ minWidth: "max-content" }}>
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="sticky left-0 bg-gray-50 z-10 px-3 py-2 text-left font-semibold text-gray-600 border border-gray-200 min-w-[140px] shadow-[2px_0_4px_-1px_rgba(0,0,0,0.08)]">
                        Task Type
                      </th>
                      {WEEKS.map((w) => (
                        <th
                          key={w}
                          className={`px-1.5 py-2 text-center font-medium border border-gray-200 min-w-[52px] ${
                            w === curWeek ? "bg-blue-50 text-[#009fe3]" : "text-gray-500"
                          }`}
                        >
                          {weekLabel(batch.startingDate, w)}
                          {w === curWeek && <div className="text-[9px] text-[#009fe3] font-bold">now</div>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {TASK_TYPES.map((task, idx) => (
                      <tr key={task.key} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50/40"}>
                        <td className="sticky left-0 bg-inherit z-10 px-3 py-2 font-medium text-gray-700 border border-gray-200 shadow-[2px_0_4px_-1px_rgba(0,0,0,0.08)] whitespace-nowrap">
                          {task.label}
                        </td>
                        {WEEKS.map((w) => (
                          <td key={w} className={`px-1 py-1.5 text-center border border-gray-100 ${w === curWeek ? "bg-blue-50/30" : ""}`}>
                            <CheckboxCell batchId={batch.batchId} w={w} task={task} map={map} saving={saving} onToggle={handleToggle} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MentorTaskCheckinPage;
